import newOrderSound from "@assets/sounds/here-at-sm.wav";
import { useSavedAccount } from "@saleor/components/AppLayout/SavedAccountContext";
import { IMessageContext } from "@saleor/components/messages";
import { DEMO_MODE, IS_TOKEN_FROM_COOKIES } from "@saleor/config";
import { User } from "@saleor/fragments/types/User";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { getMutationStatus } from "@saleor/misc";
import {
  isSupported as isCredentialsManagementAPISupported,
  login as loginWithCredentialsManagementAPI,
  saveCredentials
} from "@saleor/utils/credentialsManagement";
import { getStaffUserType } from "@saleor/utils/smop";
import ApolloClient from "apollo-client";
import Cookies from "js-cookie";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useApolloClient, useMutation } from "react-apollo";
import { IntlShape, useIntl } from "react-intl";
import OneSignal from "react-onesignal";
import { useNavigate } from "react-router-dom";

import { UserContext } from "./";
import { isSuperUser } from "./misc";
import {
  tokenAuthMutation,
  tokenRefreshMutation,
  tokenVerifyMutation
} from "./mutations";
import { RefreshToken, RefreshTokenVariables } from "./types/RefreshToken";
import { TokenAuth, TokenAuthVariables } from "./types/TokenAuth";
import { VerifyToken, VerifyTokenVariables } from "./types/VerifyToken";
import {
  getPermissionDefaultPath,
  getUserTypeDefaultPath,
  userBusinessDetailsPath,
  userLoginPath
} from "./urls";
import {
  displayDemoMessage,
  getTokenCookies,
  getTokens,
  removeTokenCookies,
  removeTokens,
  setAuthToken,
  setTokenCookies,
  setTokens
} from "./utils";

const persistToken = false;

export function useAuthProvider(
  intl: IntlShape,
  notify: IMessageContext,
  apolloClient: ApolloClient<any>
) {
  const [userContext, setUserContext] = useState<undefined | User>(undefined);
  const autologinPromise = useRef<Promise<any>>();
  const refreshPromise = useRef<Promise<boolean>>();

  const { setRecentUser, logSavedAccount, setUser } = useSavedAccount();

  useEffect(() => {
    setUser(userContext);

    return () => {
      setUser(undefined);
    };
  }, [userContext]);

  const navigate = useNavigator();
  const useNavigateState = useNavigate();
  const logout = () => {
    if (userContext !== undefined && userContext !== null) {
      setRecentUser({
        email: userContext.email,
        firstName: userContext.firstName,
        lastName: userContext.lastName
      });
    }

    setUserContext(undefined);
    if (isCredentialsManagementAPISupported) {
      navigator.credentials.preventSilentAccess();
    }
    removeTokenCookies();
    removeTokens();
  };

  const [tokenAuth, tokenAuthResult] = useMutation<
    TokenAuth,
    TokenAuthVariables
  >(tokenAuthMutation, {
    client: apolloClient,
    onCompleted: result => {
      if (result.tokenCreate.errors.length > 0) {
        logout();
      }

      const { user, token, csrfToken } = result.tokenCreate;

      if (!token && user.id && user.email) {
        const mfaLocalStorage = {
          defaultMfaMethod: user.defaultMfaMethod,
          mfaRemainingGraceDays: user.mfaRemainingGraceDays,
          userContactNo: user.contactNo,
          userEmail: user.email
        };

        localStorage.setItem(
          "mfaAuthLocalStorage",
          JSON.stringify(mfaLocalStorage)
        );
        useNavigateState("/mfa-verification-page/", { state: mfaLocalStorage });
        return;
      }

      // FIXME: Now we set state also when auth fails and returned user is
      // `null`, because the LoginView uses this `null` to display error.
      setUserContext(user);

      if (!user) {
        return;
      }

      if (IS_TOKEN_FROM_COOKIES) {
        setTokenCookies({
          auth: token,
          csrf: csrfToken,
          userId: user.id
        });
      } else {
        setTokens(token, csrfToken, persistToken);
      }
    },
    onError: logout
  });
  const [tokenRefresh] = useMutation<RefreshToken, RefreshTokenVariables>(
    tokenRefreshMutation,
    {
      client: apolloClient,
      onError: logout
    }
  );
  const [tokenVerify, tokenVerifyResult] = useMutation<
    VerifyToken,
    VerifyTokenVariables
  >(tokenVerifyMutation, {
    client: apolloClient,
    onCompleted: result => {
      if (result.tokenVerify === null) {
        logout();
      } else {
        const user = result.tokenVerify?.user;

        if (!!user) {
          setUserContext(user);
        }
      }
    },
    onError: logout
  });

  const tokenAuthOpts = {
    ...tokenAuthResult,
    status: getMutationStatus(tokenAuthResult)
  };
  const tokenVerifyOpts = {
    ...tokenVerifyResult,
    status: getMutationStatus(tokenVerifyResult)
  };

  const onLogin = () => {
    if (DEMO_MODE) {
      displayDemoMessage(intl, notify);
    }
  };

  useEffect(() => {
    const token = IS_TOKEN_FROM_COOKIES
      ? getTokenCookies().auth
      : getTokens().auth;
    if (!!token && !userContext) {
      autologinPromise.current = tokenVerify({ variables: { token } });
    } else {
      autologinPromise.current = loginWithCredentialsManagementAPI(login);
    }
  }, []);

  const login = async (email: string, password: string) => {
    const result = await tokenAuth({ variables: { email, password } });

    if (result && !result.data.tokenCreate.errors.length) {
      const { user, token, csrfToken } = result.data.tokenCreate;
      if (!token && user.id && user.email) {
        const mfaLocalStorage = {
          defaultMfaMethod: user.defaultMfaMethod,
          mfaRemainingGraceDays: user.mfaRemainingGraceDays,
          userContactNo: user.contactNo,
          userEmail: user.email
        };

        localStorage.setItem(
          "mfaLocalStorage",
          JSON.stringify(mfaLocalStorage)
        );
        Cookies.set("mfaContactNo", user.contactNo);
        Cookies.set("mfaSetupDate", user.mfaSetupDate);
        Cookies.remove("hasMaxAttemptsEmail");
        Cookies.remove("hasMaxAttemptsPhone");

        if (user.mfaSetupDate) {
          const cooldownSeconds =
            result.data.tokenCreate.data.resendCooldown || 300;
          Cookies.set("mfaCreatedAt", result.data.tokenCreate.data.createdAt, {
            expires: cooldownSeconds / 86400
          });
          Cookies.set("mfaResendCooldown", cooldownSeconds.toString(), {
            expires: cooldownSeconds / 86400
          });

          if (user.defaultMfaMethod === "EMAIL") {
            useNavigateState("/mfa-email-input-page/", {
              state: {
                createdAt: result.data.tokenCreate.data.createdAt,
                email: user.email,
                isEmailSendCode:
                  user.defaultMfaMethod === "EMAIL" ? true : false
              }
            });
            if (
              result.data.tokenCreate.data.mfaAttempts >= 3 ||
              result.data.tokenCreate.data.lockedAt
            ) {
              Cookies.set("EmailLockedOut", "true");
            }
          } else {
            if (
              result.data.tokenCreate.data.mfaAttempts >= 3 ||
              result.data.tokenCreate.data.lockedAt
            ) {
              Cookies.set("PhoneLockedOut", "true");
            }
            useNavigateState("/mfa-phone-input-page/", {
              state: {
                createdAt: result.data.tokenCreate.data.createdAt,
                email: user.email,
                isEmailSendCode:
                  user.defaultMfaMethod === "PHONE" ? true : false
              }
            });
          }
        } else {
          Cookies.remove("PhoneLockedOut");
          Cookies.remove("EmailLockedOut");
          Cookies.remove("lockoutUntil");
          useNavigateState("/mfa-verification-page/", {
            state: mfaLocalStorage
          });
        }

        return;
      }
      if (user && user.status === "INACTIVE") {
        notify({
          status: "error",
          text: "You're currently status is inactive!"
        });
        removeTokenCookies();
        navigate(userLoginPath);
        logout();
        return false;
      }

      const hasSellerMaster = user.permissionGroups.filter(
        permission => permission.smopUsertypeCode === "SELLER_MASTER"
      );

      logSavedAccount(user, token, csrfToken);
      if (
        (user.accessGroup == null && hasSellerMaster.length) ||
        (user.businessDetails && user.businessDetails.status === "RVW")
      ) {
        navigate(userBusinessDetailsPath);
      } else {
        const defaultPath = getUserTypeDefaultPath(user);

        // Redirects user based on its user type
        if (defaultPath && !isSuperUser(user)) {
          navigate(defaultPath.path);
          return;
        }

        const defaultPermissionPath = getPermissionDefaultPath(user);
        // Redirects user based on Permission
        if (defaultPermissionPath && !isSuperUser(user)) {
          navigate(defaultPermissionPath.path);
          return;
        }

        navigate("/");
      }
      if (!!onLogin) {
        onLogin();
      }

      saveCredentials(result.data.tokenCreate.user, password);
      return result.data.tokenCreate.user;
    } else {
      const { errors } = result.data.tokenCreate;

      if (errors && errors[0] && errors[0].code === "USER_UNVERIFIED") {
        Cookies.set("currentUser", JSON.stringify(errors[0].user));
        Cookies.set("currentUserPass", password);
        navigate("/otp/" + errors[0].user.id);
        notify({
          status: "error",
          text: errors[0].message
        });
      }
    }

    return null;
  };

  const loginByToken = (auth: string, refresh: string, user: User) => {
    setUserContext(user);
    if (IS_TOKEN_FROM_COOKIES) {
      setTokenCookies({ auth, csrf: refresh, userId: user.id });
    } else {
      setTokens(auth, refresh, persistToken);
    }
    logSavedAccount(user, auth, refresh);
  };

  const refreshToken = (): Promise<boolean> => {
    if (!!refreshPromise.current) {
      return refreshPromise.current;
    }

    return new Promise(resolve => {
      const token = IS_TOKEN_FROM_COOKIES
        ? getTokenCookies().csrf
        : getTokens().csrf;

      return tokenRefresh({ variables: { token } }).then(refreshData => {
        if (!!refreshData.data.tokenRefresh?.token) {
          setAuthToken(refreshData.data.tokenRefresh.token, persistToken);
          return resolve(true);
        }

        return resolve(false);
      });
    });
  };

  return {
    autologinPromise,
    login,
    loginByToken,
    logout,
    refreshToken,
    tokenAuthOpts,
    tokenVerify,
    tokenVerifyOpts,
    userContext
  };
}

interface AuthProviderProps {
  children: React.ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const apolloClient = useApolloClient();
  const intl = useIntl();
  const notify = useNotifier();
  const authToken = IS_TOKEN_FROM_COOKIES
    ? getTokenCookies().auth
    : getTokens().auth;

  const {
    login,
    loginByToken,
    logout,
    tokenAuthOpts,
    refreshToken,
    tokenVerifyOpts,
    userContext
  } = useAuthProvider(intl, notify, apolloClient);

  // TODO: Make type safe, i.e apply interface
  let enhancedUserContext;
  if (userContext) {
    const userType = getStaffUserType({
      accessGroup: userContext?.accessGroup || undefined
    });
    enhancedUserContext = { ...userContext, userType };
  }
  const hasOneSignal =
    process.env.ONESIGNAL_APP_ID !== undefined &&
    process.env.ONESIGNAL_SAFARI_WEB_ID !== undefined;
  React.useEffect(() => {
    if (hasOneSignal) {
      OneSignal.init({
        appId: process.env.ONESIGNAL_APP_ID,
        notifyButton: { enable: true },
        safari_web_id: process.env.ONESIGNAL_SAFARI_WEB_ID
      });
    }
  }, []);

  function isIOS() {
    return (
      [
        "iPad Simulator",
        "iPhone Simulator",
        "iPod Simulator",
        "iPad",
        "iPhone",
        "iPod"
      ].includes(navigator.platform) ||
      // iPad on iOS 13 detection
      (navigator.userAgent.includes("Mac") && "ontouchend" in document)
    );
  }

  React.useEffect(() => {
    if (
      hasOneSignal &&
      userContext &&
      userContext.accessGroup &&
      userContext.accessGroup.smopUsertypeCode &&
      userContext.accessGroup.smopUsertypeCode === "STORE_ADMIN"
    ) {
      OneSignal.showNativePrompt();
      OneSignal.on("subscriptionChange", function(isSubscribed) {
        if (isSubscribed) {
          OneSignal.sendTags({
            userEmail: userContext.email,
            userId: userContext.accessGroup.accesses[0].objectId,
            userType: userContext.accessGroup.smopUsertypeCode
          });
        } else {
          OneSignal.deleteTags(["userType", "userEmail", "userId"]);
        }
      });
      OneSignal.on("notificationDisplay", function() {
        const audio = new Audio(newOrderSound);
        audio.play();
      });
      if (!isIOS() && typeof OneSignal.setSubscription === "function") {
        OneSignal.setSubscription(true);
      }
    } else {
      if (
        authToken === null &&
        hasOneSignal &&
        !isIOS() &&
        typeof OneSignal.setSubscription === "function"
      ) {
        OneSignal.setSubscription(false);
      }
    }
  }, [userContext]);

  return (
    <UserContext.Provider
      value={{
        login,
        loginByToken,
        logout,
        tokenAuthLoading: tokenAuthOpts.loading,
        tokenRefresh: refreshToken,
        tokenVerifyLoading: tokenVerifyOpts.loading,
        user: enhancedUserContext,
        userDetail: userContext
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useAuth = () => {
  const user = useContext(UserContext);
  const isAuthenticated = !!user.user;

  const token = IS_TOKEN_FROM_COOKIES ? getTokenCookies() : getTokens();

  return {
    hasToken: !!token.auth || !!token.csrf,
    isAuthenticated,
    tokenAuthLoading: user.tokenAuthLoading,
    tokenVerifyLoading: user.tokenVerifyLoading,
    user: user.user
  };
};

export default AuthProvider;
